import {
  Button,
  Heading,
  Square,
  Stack,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import React, { useState } from 'react';
import { Column, Copy, Flex, Row } from '@grubhubprod/gh-cookbook-react/dist';
import classnames from 'classnames';
import { IInvoice, LONG_SEARCH_DELAY } from 'src/utilities/useSearchHooks';
import { ICreditCard } from 'src/types/CreditCard';
import { Modal } from 'src/components/Modal/Modal';
import {
  currencyFormatter,
  transformPenniesToDollar,
} from 'src/utilities/currency-utils';
import { pushError, pushSuccess } from 'src/utilities/notifications';
import { sdk } from 'src/utilities/sdk';
import { getConfig } from 'src/utilities/config';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';

export interface IEnterpriseConfirmPaymentModalProps {
  corpId: string;
  invoice: IInvoice;
  card: ICreditCard;
  onSuccess: () => void;
  onClose: () => void;
}

export function EnterpriseConfirmPaymentModal({
  onClose,
  onSuccess,
  card,
  invoice,
  corpId,
}: IEnterpriseConfirmPaymentModalProps): JSX.Element {
  const { customerServiceNumber } = getConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  function getExpDateFromCard(month: string, year: string): string {
    return `${month.length < 2 ? `0${month}` : month}/${year.substring(2, 4)}`;
  }

  async function confirmPayment() {
    setIsSubmitting(true);
    try {
      const payWithCreditCardEndpoint = `/corps/${corpId}/invoice/${invoice.invoice_id}/pay_with_creditcard`;
      await sdk.http.post(payWithCreditCardEndpoint, {});
      setTimeout(() => {
        onSuccess();
        pushSuccess(`Your invoice ${invoice.invoice_number} has been paid`);
        onClose();
      }, LONG_SEARCH_DELAY);
    } catch (e) {
      pushError(
        `Your invoice ${invoice.invoice_number} was not paid. Try again.`
      );
      onClose();
    }
  }

  return (
    <Modal size="sm">
      <Square as="header" width="100%">
        <Button variant="tertiary" type="button" onClick={onClose}>
          <SvgX size="24px" />
        </Button>
        <Square inset="md">
          <Heading
            variant="h3"
            color={card.expired ? 'warning' : 'primary'}
            textAlign="center"
          >
            {card.expired ? (
              'Your credit card has expired.'
            ) : (
              <>
                {currencyFormatter(
                  Number(transformPenniesToDollar(invoice.amount_owed))
                )}{' '}
                will be charged to:
              </>
            )}
          </Heading>
        </Square>
        <Stack y="md">
          <Flex alignItems="bottom" justifyContent="center">
            <Stack
              x="x-sm"
              className={classnames('creditCardIcon', {
                [card.credit_card_type]: true,
              })}
            />
            <Flex alignItems="center">
              <Stack x="xxx-sm">{card.credit_card_type.toUpperCase()}</Stack>
              ending in {card.credit_card_last4}, exp{' '}
              {getExpDateFromCard(
                card.expiration_month.toString(),
                card.expiration_year.toString()
              )}
            </Flex>
          </Flex>
        </Stack>
        {card.expired && (
          <Stack y="md">
            <Row alignItems="center" justifyContent="center">
              <Column phone={9}>
                <Copy variant="caption" color="secondaryText">
                  Call your account manager at {customerServiceNumber} for
                  alternative methods of payment.
                </Copy>
              </Column>
            </Row>
          </Stack>
        )}
      </Square>
      <Square as="footer" dimension="2" inset="md">
        <Row gutter={8}>
          <Column gutter={8} phone={6}>
            {card.expired ? (
              <RouterButtonLink
                href={`/corporate/enterprise/${corpId}/payments`}
                buttonSize="medium"
                buttonVariant="primary"
                useBlockButton
              >
                <Flex height="100%" alignItems="center" justifyContent="center">
                  Update credit card
                </Flex>
              </RouterButtonLink>
            ) : (
              <Button
                block
                variant="primary"
                buttonSize="medium"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                onClick={confirmPayment}
              >
                Confirm Payment
              </Button>
            )}
          </Column>
          <Column gutter={8} phone={6}>
            <Button
              block
              variant="secondary"
              buttonSize="medium"
              type="button"
              disabled={isSubmitting}
              onClick={onClose}
            >
              Cancel
            </Button>
          </Column>
        </Row>
      </Square>
    </Modal>
  );
}
